import { useState } from 'react';
import type {
  Package,
  PackageItemInput,
  PackageKind,
  PackagePurpose,
  Service,
} from '@salonomy/types';
import { Button, Field, Modal, Pill, Select, TextInput, Toggle } from '@salonomy/ui';
import { useCreatePackage, useUpdatePackage } from '../../data/packages.js';
import { useToast } from '../../lib/toast.js';
import { penceToPounds, poundsToPence } from '../till/money.js';
import './packages.css';

const KINDS: Array<{ value: PackageKind; label: string }> = [
  { value: 'prepaid_sessions', label: 'Prepaid sessions (a course of N treatments)' },
  { value: 'prepaid_value', label: 'Prepaid value (a stored balance)' },
  { value: 'membership_recurring', label: 'Recurring membership (£/month — not sellable yet)' },
];

const PURPOSES: Array<{ value: PackagePurpose; label: string }> = [
  { value: 'multi', label: 'Multi-purpose (VAT at redemption)' },
  { value: 'single', label: 'Single-purpose (deferred — not sellable yet)' },
];

/** A draft line in the editor (string fields so the inputs stay controlled before parsing at submit). */
interface ItemDraft {
  key: string;
  serviceId: string;
  sessionsGranted: string;
  discountPct: string;
}

function blankItem(): ItemDraft {
  return { key: crypto.randomUUID(), serviceId: '', sessionsGranted: '', discountPct: '' };
}

/**
 * Create / edit a catalogue package + its items (manager). Two shapes, deliberately:
 *  - CREATE carries the full body (name/kind/price/validity/purpose/items). `kind` and `purpose` can be
 *    set to the DEFERRED choices (membership_recurring / single) here — they're carried for
 *    forward-compatibility — but the SELL modal won't offer them (the API 422s a sell of either). The form
 *    flags that honestly.
 *  - EDIT sends only the editable attributes (name/price/validity/purpose/kind/active). The API freezes
 *    price/purpose/kind ONCE any instrument is sold (it 409/422s); items are not edited here.
 *
 * Cost/price cross the £↔pence edge via the till's poundsPence at the form boundary only. A per-line
 * "discount %" is converted to discountBps (×100) at submit — never business math, just a UI-edge parse.
 * Remaining sessions/value are SERVER-derived on the sold instrument; nothing here computes a balance.
 */
export function PackageEditorModal({
  existing,
  services,
  onClose,
}: {
  existing?: Package;
  services: Service[];
  onClose: () => void;
}) {
  const create = useCreatePackage();
  const update = useUpdatePackage();
  const toast = useToast();
  const editing = Boolean(existing);

  const [name, setName] = useState(existing?.name ?? '');
  const [kind, setKind] = useState<PackageKind>(existing?.kind ?? 'prepaid_sessions');
  const [purpose, setPurpose] = useState<PackagePurpose>(existing?.purpose ?? 'multi');
  const [price, setPrice] = useState(penceToPounds(existing?.pricePence ?? 0));
  const [validityDays, setValidityDays] = useState(
    existing?.validityDays != null ? String(existing.validityDays) : '',
  );
  const [active, setActive] = useState(existing?.active ?? true);
  // CREATE only — items are not edited after creation (the API doesn't expose a per-item PATCH here).
  const [items, setItems] = useState<ItemDraft[]>(
    existing
      ? existing.items.map((it) => ({
          key: it.id,
          serviceId: it.serviceId ?? '',
          sessionsGranted: it.sessionsGranted != null ? String(it.sessionsGranted) : '',
          discountPct: it.discountBps != null ? String(it.discountBps / 100) : '',
        }))
      : [blankItem()],
  );
  const [busy, setBusy] = useState(false);

  const isDeferred = kind === 'membership_recurring' || purpose === 'single';

  function setItem(key: string, patch: Partial<ItemDraft>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((prev) => [...prev, blankItem()]);
  }
  function removeItem(key: string) {
    setItems((prev) => prev.filter((it) => it.key !== key));
  }

  async function submit() {
    if (!name.trim()) {
      toast.error('A package name is required.');
      return;
    }
    const pricePence = poundsToPence(price);
    if (pricePence == null || pricePence <= 0) {
      toast.error('A package price greater than £0 is required.');
      return;
    }
    const validity = validityDays.trim()
      ? Math.max(1, Math.trunc(Number(validityDays) || 0))
      : null;

    setBusy(true);
    try {
      if (editing && existing) {
        // EDIT sends only attributes. The API freezes price/purpose/kind once any instrument is sold —
        // we send them and let the server be the source of truth (it 409/422s a frozen change faithfully).
        await update.mutateAsync({
          id: existing.id,
          body: {
            name: name.trim(),
            pricePence,
            validityDays: validity,
            purpose,
            kind,
            active,
          },
        });
        toast.success('Package updated');
      } else {
        // Parse the item drafts into the typed PackageItemInput[]: an empty serviceId means "any service";
        // sessions/discount are omitted when blank (the server treats null as unset).
        const parsedItems: PackageItemInput[] = items.map((it) => {
          const sessions = it.sessionsGranted.trim()
            ? Math.max(1, Math.trunc(Number(it.sessionsGranted) || 0))
            : null;
          const pct = it.discountPct.trim() ? Number(it.discountPct) : null;
          // discount % → bps (×100), clamped to the API's 0–10000 range; a UI-edge parse, not money math.
          const discountBps =
            pct != null && Number.isFinite(pct)
              ? Math.min(10000, Math.max(0, Math.round(pct * 100)))
              : null;
          return {
            serviceId: it.serviceId || null,
            sessionsGranted: sessions,
            discountBps,
          };
        });
        await create.mutateAsync({
          name: name.trim(),
          kind,
          pricePence,
          validityDays: validity,
          purpose,
          items: parsedItems,
        });
        toast.success('Package created');
      }
      onClose();
    } catch {
      toast.error('Could not save the package.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit package' : 'New package'}
      width={580}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {editing ? 'Save' : 'Create package'}
          </Button>
        </>
      }
    >
      <Field label="Name">
        <TextInput
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 6-session blow-dry course"
        />
      </Field>

      <div className="pkg-modal-grid">
        <Field label="Kind" hint="What the instrument grants.">
          <Select value={kind} onChange={(e) => setKind(e.target.value as PackageKind)}>
            {KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="VAT class" hint="Multi-purpose: VAT lands at redemption.">
          <Select value={purpose} onChange={(e) => setPurpose(e.target.value as PackagePurpose)}>
            {PURPOSES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="pkg-modal-grid">
        <Field label="Price (£)" hint="The catalogue list price (VAT-inclusive).">
          <TextInput
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
          />
        </Field>
        <Field label="Validity (days)" hint="Blank = never expires.">
          <TextInput
            inputMode="numeric"
            value={validityDays}
            onChange={(e) => setValidityDays(e.target.value)}
            placeholder="e.g. 365"
          />
        </Field>
      </div>

      {isDeferred ? (
        <div className="pkg-note pkg-note--warn">
          <Pill tone="neutral">Not sellable yet</Pill>
          <span>
            {kind === 'membership_recurring'
              ? 'Recurring memberships are a separate workflow — you can define this package, but selling it is rejected for now.'
              : 'Single-purpose packages are deferred (a VAT design that needs accountant sign-off) — you can define this, but selling it is rejected for now.'}
          </span>
        </div>
      ) : null}

      {editing ? (
        <div className="pkg-toggles">
          <Toggle checked={active} onChange={setActive} label="Active (offered for sale)" />
        </div>
      ) : (
        <div className="pkg-items">
          <div className="pkg-items__head">
            <div className="section-title" style={{ margin: 0 }}>
              What it grants
            </div>
            <Button size="sm" variant="ghost" onClick={addItem}>
              Add item
            </Button>
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginBottom: 8, lineHeight: 1.5 }}>
            Each item names a service (or “any service”), how many sessions it grants, and an
            optional per-line discount. A value package can be left as a single “any service” item
            with no sessions.
          </div>
          {items.map((it) => (
            <div key={it.key} className="pkg-item-row">
              <Field label="Service">
                <Select
                  value={it.serviceId}
                  onChange={(e) => setItem(it.key, { serviceId: e.target.value })}
                >
                  <option value="">Any service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Sessions">
                <TextInput
                  inputMode="numeric"
                  value={it.sessionsGranted}
                  onChange={(e) => setItem(it.key, { sessionsGranted: e.target.value })}
                  placeholder="—"
                />
              </Field>
              <Field label="Discount %">
                <TextInput
                  inputMode="decimal"
                  value={it.discountPct}
                  onChange={(e) => setItem(it.key, { discountPct: e.target.value })}
                  placeholder="0"
                />
              </Field>
              <button
                type="button"
                className="pkg-item-row__del"
                aria-label="Remove item"
                onClick={() => removeItem(it.key)}
                disabled={items.length === 1}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <div className="pkg-note muted">
          Items aren&rsquo;t edited here. Price, VAT class and kind are frozen once any instrument
          has been sold against this package — the server keeps a sold course at the price it was
          bought at.
        </div>
      ) : null}
    </Modal>
  );
}
